import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { useDebounce } from '../hooks/useDebounce';
import { logLoginFailed, logPasswordResetRequest } from '../utils/logger';

import SecureInput from '../components/common/SecureInput';

const LoginScreen = ({ disabledMessage, clearDisabledMessage }) => {
    const [email, setEmail] = useState(''); 
    const [password, setPassword] = useState(''); 
    const [error, setError] = useState(disabledMessage ? 'This account has been disabled. Please contact your administrator.' : '');
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState('');

    // Keep inputs controlled: always set string values

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        if (clearDisabledMessage) clearDisabledMessage();
        
        // Add input validation
        if (!email || !password) {
            setError('Email and password are required');
            setLoading(false);
            return;
        }

        if (!email.includes('@')) {
            setError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        try {
            await signInWithEmailAndPassword(auth, email, password);
            // Note: Successful login is logged in AuthContext when user state changes
        } catch (err) {
            console.error('Login error:', err.code); // Log for debugging
            
            // Log failed login attempt to security logs
            let failureReason = 'Unknown error';
            let userMessage = 'Login failed. Please try again';
            
            switch (err.code) {
                case 'auth/user-not-found':
                    failureReason = 'User not found';
                    userMessage = 'Invalid email or password';
                    break;
                case 'auth/wrong-password':
                    failureReason = 'Wrong password';
                    userMessage = 'Invalid email or password';
                    break;
                case 'auth/invalid-credential':
                    failureReason = 'Invalid credentials';
                    userMessage = 'Invalid email or password';
                    break;
                case 'auth/too-many-requests':
                    failureReason = 'Too many attempts';
                    userMessage = 'Too many failed attempts. Please try again later';
                    break;
                case 'auth/user-disabled':
                    failureReason = 'Account disabled';
                    userMessage = 'This account has been disabled';
                    break;
                default:
                    failureReason = err.code || 'Unknown error';
            }
            
            // Log to security logs (async, don't block UI)
            logLoginFailed(email, failureReason).catch(e => 
                console.warn('Failed to log security event:', e)
            );
            
            setError(userMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setResetSuccess('');
        
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        if (!email.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }

        setResetLoading(true);

        try {
            await sendPasswordResetEmail(auth, email);
            setResetSuccess('Password reset email sent! Check your inbox.');
            setError('');
            
            // Log password reset request (async, don't block UI)
            logPasswordResetRequest(email).catch(e => 
                console.warn('Failed to log security event:', e)
            );
        } catch (err) {
            console.error('Password reset error:', err.code);
            
            switch (err.code) {
                case 'auth/user-not-found':
                    setError('No account found with this email address');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid email address');
                    break;
                case 'auth/too-many-requests':
                    setError('Too many requests. Please try again later');
                    break;
                default:
                    setError('Failed to send reset email. Please try again');
            }
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen font-sans">
            <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
                <div className="text-center">
                    <div className="flex items-center justify-center mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-indigo-600">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg>
                        <span className="ml-2 text-2xl font-bold">ApparelCRM</span>
                    </div>
                    <h2 className="text-xl text-gray-600">Welcome back! Please sign in.</h2>
                </div>
                {error && <p className="text-center text-red-500 text-sm">{error}</p>}
                {resetSuccess && <p className="text-center text-green-600 text-sm">{resetSuccess}</p>}
                <form className="mt-8 space-y-6" onSubmit={handleLogin}>
                    <SecureInput
                        type="email"
                        value={email ?? ''}
                        onChange={(v) => {
                            // Support either string value or event
                            const val = typeof v === 'string' ? v : (v?.target?.value ?? '');
                            setEmail(String(val || ''));
                        }}
                        placeholder="Email"
                        maxLength={100}
                    />
                    <SecureInput
                        type="password"
                        value={password ?? ''}
                        onChange={(v) => {
                            const val = typeof v === 'string' ? v : (v?.target?.value ?? '');
                            setPassword(String(val || ''));
                        }}
                        placeholder="Password"
                        maxLength={100}
                    />
                    <button 
                        type="submit"
                        disabled={loading}
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        {loading ? 'Signing in...' : 'Sign in'}
                    </button>
                    
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={handleForgotPassword}
                            disabled={resetLoading}
                            className="text-sm text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                        >
                            {resetLoading ? 'Sending...' : 'Forgot password?'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginScreen;
